import {
  buildClientDevice,
  publishOrtb2,
  publisherDomain,
  toOrtb2,
} from '@redbrickmedia/duplex.user-identity'
import { z } from 'zod'
import debug from '../../common/utils/console'
import { preAYScripts } from '../utils/ad-ops/preAYScripts'
import { whenAyReady } from '../utils/ad-ops/assertive-yield/queue'
import { resolveConsent } from '../utils/consent/consentGate'
import {
  markConsentTiming,
  measureConsentTiming,
} from '../utils/consent/consentTiming'

const logger = debug.withTag('plugins:filters')

export const UserFilteringSchema = z.object({
  country: z.string(),
  region: z.string().optional(),
  regionName: z.string().optional(),
  city: z.string().default(''),
  postalCode: z.string().optional(),
  metroCode: z.string().optional(),
  continent: z.string().optional(),
  timezone: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  userAgent: z.string().nullable(),
  isMobile: z.boolean().default(false),
  os: z.string().optional(),
})

export type UserFilteringData = z.infer<typeof UserFilteringSchema>

const defaultUserFilteringData = UserFilteringSchema.parse({
  country: 'US',
  userAgent: null,
  isMobile: false,
})

type ClientDevice = ReturnType<typeof buildClientDevice>

function articleIdOf(route: { params: Record<string, unknown> }) {
  const id = route.params.id
  return (Array.isArray(id) ? id[0] : id) ?? null
}

/**
 * Hands the geo and device we just resolved to Prebid as ORTB first-party data.
 *
 * Queued rather than published directly: Assertive Yield applies its own Prebid
 * config once on load and the queue drains after, so anything set before that
 * would be overwritten.
 */
function publishFirstPartyData(geo: UserFilteringData, device: ClientDevice) {
  const domain = publisherDomain()
  if (!domain) {
    return
  }

  const { siteName } = useRuntimeConfig().public
  const name = typeof siteName === 'string' && siteName ? siteName : domain

  const publish = (articleId: string | null) => {
    try {
      const ortb2 = toOrtb2(
        { geo, device },
        { domain, name, page: window.location.href },
        articleId ? { id: articleId } : undefined
      )

      whenAyReady(() => publishOrtb2(ortb2))
    } catch (error) {
      // Ads must load whether or not first-party data does.
      logger.warn('Failed to publish ORTB first-party data', error)
    }
  }

  publish(articleIdOf(useRoute()))

  // Articles arrive by client-side navigation. publishOrtb2 replaces
  // site.content rather than appending.
  useRouter().afterEach((to) => publish(articleIdOf(to)))
}

/**
 * The visitor's geo from /api/v2/filters with the device read locally, and the
 * country when the response gave one. Falls back to the defaults otherwise.
 */
async function fetchFilters(
  device: ClientDevice
): Promise<{ filters: UserFilteringData; country?: string }> {
  try {
    const geo = await $fetch<Record<string, unknown>>('/api/v2/filters')
    const parsed = UserFilteringSchema.safeParse({
      ...geo,
      userAgent: device.ua ?? null,
      isMobile: device.isMobile,
      ...(device.os ? { os: device.os } : {}),
    })
    if (parsed.success) {
      return { filters: parsed.data, country: parsed.data.country }
    }
    logger.warn('Invalid user filtering data:', parsed.error)
    return { filters: defaultUserFilteringData }
  } catch (error) {
    logger.warn('Error fetching user filtering data:', error)
    // Geo is gone, but the device half was read locally and still holds.
    return {
      filters: {
        ...defaultUserFilteringData,
        userAgent: device.ua ?? null,
        isMobile: device.isMobile,
      },
    }
  }
}

/**
 * Fetches user filtering data from the server and provides it to the Nuxt app,
 * then publishes it into the bid stream.
 */
export default defineNuxtPlugin(async (nuxtApp) => {
  markConsentTiming('filters-start')
  // The OpenFeature plugin runs right after this one and its first act is a
  // dynamic import of the ConfigCat client chunk. Start that download now so
  // it overlaps the filters round trip instead of following it. The module
  // cache makes the later import instant.
  import('../openfeature/client.web').catch(() => {})

  const device = buildClientDevice()

  // Head of the ad chain. Started here so it overlaps the filters round trip
  // instead of following it. preAYScripts is memoized, so the assertive plugin
  // waits on this same load.
  void preAYScripts()

  const { filters, country } = await fetchFilters(device)
  nuxtApp.provide('filters', filters)
  // No country rather than the default one when geo failed: such a visitor
  // must not get the US default.
  void resolveConsent(country)
  publishFirstPartyData(filters, device)
  markConsentTiming('filters-settled')
  measureConsentTiming('filters', 'filters-start', 'filters-settled')
})
